import { useForm } from 'react-hook-form';

import classNames from 'classnames';
import { z } from 'zod';

import { useAuth } from '@shared/common/providers/AuthProvider';

import { useUpdateUsernameMutation } from '@query/user';

import { zodResolver } from '@hookform/resolvers/zod';
import { ExtendedConfirmModal } from '@ui/uikit/components/common/ExtendedConfirmModal';
import { getErrorMessage } from '@utilities/errors/getErrorMessage';

import { CompleteYourMagicIdForm } from './CompleteYourMagicIdForm';

type Form = {
  username: string;
  email: string;
};

const formSchema = z.object({
  username: z
    .string()
    .min(4)
    .max(20)
    .regex(
      /^[a-zA-Z0-9_]+$/,
      'The username must contain only latin letters, numbers and the symbol "_"',
    )
    .optional(),
});

interface Props {
  isOpenMagicIDModal: boolean;
  hideInputFields?: boolean;
  formIsDisabled?: boolean;
  updateModalState: (isOpen: boolean) => void;
  onSubmit: () => void;
  showEmailInputOnly?: boolean;
}

export const CompleteYourMagicIdModal = ({
  isOpenMagicIDModal,
  hideInputFields,
  formIsDisabled,
  updateModalState,
  onSubmit,
  showEmailInputOnly,
}: Props) => {
  const { user } = useAuth();

  const form = useForm<Form>({
    resolver: zodResolver(formSchema),
    disabled: formIsDisabled,
    defaultValues: {
      username: user?.attributes.name,
      email: user?.attributes.email,
    },
  });

  const { formState, handleSubmit, setError, reset } = form;
  const { mutateAsync: updateUsername, isPending: isUpdatingUserName } =
    useUpdateUsernameMutation();

  const onSubmitHandler = handleSubmit(async (data) => {
    if (data.username && !user?.attributes.name) {
      try {
        await updateUsername(data.username);

        reset(data);
      } catch (error) {
        setError('username', {
          type: 'backend',
          message: getErrorMessage(error),
        });

        return;
      }
    }

    onSubmit();
  });

  const hasErrors = Object.keys(formState.errors).length > 0;

  return (
    <ExtendedConfirmModal
      open={isOpenMagicIDModal}
      onOpenChange={updateModalState}
      title="Complete your Magic ID"
      confirmLabel="Done"
      onClick={onSubmitHandler}
      confirmDisabled={hasErrors}
      description={
        <div
          className={classNames('flex flex-col items-start gap-3', {
            'msq-skeleton-target': !user,
          })}
        >
          <p className="text-base-text-secondary text-caption-sm-a text-left">
            In order to participate in the process, please complete the set-up of your Magic ID.
          </p>
          {!showEmailInputOnly && (
            <p className="text-base-text-secondary text-caption-sm-a text-left">
              Your username cannot be changed once applied, and your EVM wallet is permanently
              linked to this account.
            </p>
          )}
          <p className="mb-6 text-base-text-secondary text-caption-sm-a text-left">
            You can change the rest of profile information in your Magic ID.
          </p>
        </div>
      }
    >
      {user ? (
        <CompleteYourMagicIdForm
          form={form}
          user={user}
          onSubmitHandler={onSubmitHandler}
          hideInputFields={hideInputFields}
          disabled={formIsDisabled}
          showEmailInputOnly={showEmailInputOnly}
          isUpdatingUserName={isUpdatingUserName}
        />
      ) : null}
    </ExtendedConfirmModal>
  );
};
